import React, { useState } from 'react';
import {
  Button,
  Modal,
  FileInput,
  Text,
  Stack,
  Group,
  Alert,
  Loader,
  Badge,
  Card,
  Title,
  Divider,
  List,
  ThemeIcon,
  NumberFormatter,
  Table
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconFileSpreadsheet, IconCheck, IconAlertCircle, IconX } from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { PortfolioWarning, TradeAllocation } from '../../types';

const TRADE_ID_PREFIX = '965080_';


interface ParsedData {
  tradeAllocations: Map<string, TradeAllocation[]>;
  totalTrades: number;
  totalAllocations: number;
  totalAllocationsQty: number;
  processedSheets: string[];
  portfolioSummary: Map<string, number>;
}

interface ExcelUploadComponentProps {
  onAllocationsUpdate: (allocations: Map<string, TradeAllocation[]>) => Promise<PortfolioWarning[]>;
  loading?: boolean;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

const ExcelUploadComponent: React.FC<ExcelUploadComponentProps> = ({
  onAllocationsUpdate,
  loading = false,
  opened,
  setOpened
}) => {
  // const [opened, { open, close }] = useDisclosure(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<PortfolioWarning[]>([]);

  const checkSheetStructure = (worksheet: XLSX.WorkSheet): boolean => {
    try {
      if (!worksheet || !worksheet['!ref']) {
        return false;
      }

      const range = XLSX.utils.decode_range(worksheet['!ref']);

      // Check if sheet has at least 4 rows (headers + some data)
      if (range.e.r < 3) {
        return false;
      }

      // Look for "Trade #" in row 3
      let hasTradeColumn = false;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const headerCell = worksheet[XLSX.utils.encode_cell({ r: 2, c: col })]; // Row 3 (0-indexed)
        if (headerCell && headerCell.v === 'Trade #') {
          hasTradeColumn = true;
          break;
        }
      }

      if (!hasTradeColumn) {
        return false;
      }

      // Look for numeric portfolio IDs in row 2
      let portfolioCount = 0;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const portfolioIdCell = worksheet[XLSX.utils.encode_cell({ r: 1, c: col })]; // Row 2 (0-indexed)
        if (portfolioIdCell && portfolioIdCell.v && typeof portfolioIdCell.v === 'number') {
          portfolioCount++;
        }
      }

      return portfolioCount > 0;

    } catch (error) {
      return false;
    }
  };

  const extractPortfolioColumns = (worksheet: XLSX.WorkSheet, range: XLSX.Range) => {
    const portfolioColumns = [];

    // Scan row 2 for portfolio IDs and row 3 for portfolio names
    for (let col = range.s.c; col <= range.e.c; col++) {
      const portfolioIdCell = worksheet[XLSX.utils.encode_cell({ r: 1, c: col })]; // Row 2 (0-indexed)
      const portfolioNameCell = worksheet[XLSX.utils.encode_cell({ r: 2, c: col })]; // Row 3 (0-indexed)

      if (portfolioIdCell && portfolioIdCell.v && typeof portfolioIdCell.v === 'number') {
        const portfolioId = portfolioIdCell.v;
        const portfolioName = portfolioNameCell ? portfolioNameCell.v : 'Unknown';
        portfolioColumns.push({
          col,
          id: portfolioId.toString(), // Store as string for consistency
          name: portfolioName
        });
      }
    }

    return portfolioColumns;
  };

  const findTradeIdColumn = (worksheet: XLSX.WorkSheet, range: XLSX.Range): number => {
    // Look for "Trade #" in row 3 (0-indexed row 2)
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 2, c: col })]; // Row 3 (0-indexed)
      if (headerCell && headerCell.v === 'Trade #') {
        return col;
      }
    }

    throw new Error('Trade # column not found');
  };

  const extractTradeAllocations = (
    worksheet: XLSX.WorkSheet,
    range: XLSX.Range,
    tradeIdColumn: number,
    portfolioColumns: any[]
  ) => {
    const tradeAllocations = new Map<string, TradeAllocation[]>();

    // Start from row 4 (0-indexed row 3) to skip headers
    for (let row = 3; row <= range.e.r; row++) {
      const tradeIdCell = worksheet[XLSX.utils.encode_cell({ r: row, c: tradeIdColumn })];

      if (tradeIdCell && tradeIdCell.v) {
        const tradeId = String(tradeIdCell.v);

        const allocations: TradeAllocation[] = [];

        // Check each portfolio column for allocation
        portfolioColumns.forEach(portfolio => {
          const allocationCell = worksheet[XLSX.utils.encode_cell({ r: row, c: portfolio.col })];

          if (allocationCell && allocationCell.v && allocationCell.v !== 0) {
            const qty = Number(allocationCell.v);
            if (!isNaN(qty) && qty > 0) {
              allocations.push({ portfolioId: portfolio.id, qty: qty });
            }
          }
        });

        if (allocations.length > 0) {
          tradeAllocations.set(tradeId, allocations);
        }
      }
    }

    return tradeAllocations;
  };

  const processExcelFile = async (file: File): Promise<ParsedData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, {
            cellStyles: true,
            cellFormula: true,
            cellDates: true,
            cellNF: true,
            sheetStubs: true
          });

          const combinedMap = new Map<string, TradeAllocation[]>();
          const processedSheets: string[] = [];
          const portfolioTotals = new Map<string, number>();
          let totalAllocations = 0;
          let totalAllocationsQty = 0;

          // Process all sheets that match the structure
          for (let i = 0; i < workbook.SheetNames.length; i++) {
            const sheetName = workbook.SheetNames[i];
            const worksheet = workbook.Sheets[sheetName];

            if (checkSheetStructure(worksheet)) {
              try {
                const range = XLSX.utils.decode_range(worksheet['!ref']!);
                const portfolioColumns = extractPortfolioColumns(worksheet, range);

                if (portfolioColumns.length === 0) continue;

                const tradeIdColumn = findTradeIdColumn(worksheet, range);
                const tradeAllocations = extractTradeAllocations(worksheet, range, tradeIdColumn, portfolioColumns);

                if (tradeAllocations.size === 0) continue;

                // Merge into combined map
                for (const [tradeId, allocations] of tradeAllocations) {
                  // make sure trades from all sheets are combined
                  const existingAllocations = combinedMap.get(tradeId) || [];
                  combinedMap.set(tradeId, [...allocations, ...existingAllocations]);

                  // Update portfolio totals
                  allocations.forEach(allocation => {
                    const currentTotal = portfolioTotals.get(allocation.portfolioId) || 0;
                    // portfolioTotals.set(allocation.portfolioId, currentTotal + allocation.qty);
                    portfolioTotals.set(allocation.portfolioId, currentTotal + 1);
                    totalAllocations++;
                    totalAllocationsQty += allocation.qty;
                  });
                }

                processedSheets.push(sheetName);
              } catch (error) {
                console.warn(`Error processing sheet ${sheetName}:`, error);
              }
            }
          }

          if (combinedMap.size === 0) {
            throw new Error('No valid allocation data found in the Excel file');
          }

          resolve({
            tradeAllocations: combinedMap,
            totalTrades: combinedMap.size,
            totalAllocations,
            totalAllocationsQty,
            processedSheets,
            portfolioSummary: portfolioTotals
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setWarnings([]);

    try {
      const data = await processExcelFile(file);
      setParsedData(data);

      notifications.show({
        title: 'Success',
        message: `Parsed ${data.totalTrades} trades from ${data.processedSheets.length} sheet(s)`,
        color: 'green',
        icon: <IconCheck size="1rem" />
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        icon: <IconX size="1rem" />
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = async () => {
    if (parsedData) {
      // add '965080_' prefix to each trade ID
      const updatedAllocations = new Map<string, TradeAllocation[]>();
      parsedData.tradeAllocations.forEach((allocations, tradeId) => {
        const updatedTradeId = `${TRADE_ID_PREFIX}${tradeId}`;
        updatedAllocations.set(updatedTradeId, allocations);
      });
      setWarnings([]);
      const warnings = await onAllocationsUpdate(updatedAllocations);
      console.log("Warnings received:", warnings);
      if (warnings.length > 0) {

        setWarnings(warnings);
        console.log("Undoing allocations due to warnings:", warnings);
        // notifications.show({
        //   title: 'Allocations Updated',
        //   message: `${parsedData.totalTrades} trades undo due to warnings:\n${warnings.join('\n')}`,
        //   color: 'red',
        //   icon: <IconX size="1rem" />
        // });
        setFile(null);
        setParsedData(null);
        setError(null);
        // setOpened(false);
      } else {
        notifications.show({
          title: 'Allocations Updated',
          message: `${parsedData.totalTrades} trades have been processed`,
          color: 'blue',
          icon: <IconCheck size="1rem" />
        });

        // Reset and close
        setFile(null);
        setParsedData(null);
        setError(null);
        setOpened(false);
        setWarnings([]);
        // close();
      }



    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData(null);
    setError(null);
    setOpened(false);
    setWarnings([]);
    // close();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => { handleClose() }}
        title={
          <Group>
            <IconUpload size="1.2rem" />
            <Title order={4}>Upload Trade Allocations</Title>
          </Group>
        }
        size="xl"
        centered
      >
        <Stack gap="md">
          <FileInput
            label="Select Excel File"
            placeholder="Choose .xls or .xlsx file"
            accept=".xls,.xlsx"
            value={file}
            onChange={setFile}
            leftSection={<IconFileSpreadsheet size="1rem" />}
            disabled={isProcessing}
          />

          {file && !parsedData && !isProcessing && (
            <Button
              onClick={handleFileUpload}
              loading={isProcessing}
              fullWidth
            >
              Process File
            </Button>
          )}

          {isProcessing && (
            <Group justify="center">
              <Loader size="sm" />
              <Text>Processing Excel file...</Text>
            </Group>
          )}

          {error && (
            <Alert
              icon={<IconAlertCircle size="1rem" />}
              title="Error"
              color="red"
            >
              {error}
            </Alert>
          )}

          {warnings.length > 0 && (
            <>
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                title="Warnings"
                color="red"
              >
                Some trades could not be allocated due to warnings. Please review the details below.
              </Alert>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th ta="center">Portfolio</Table.Th>
                    <Table.Th ta="center">Trade ID</Table.Th>
                    <Table.Th ta="center">Warning</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {warnings.map(p => {
                    return (<Table.Tr key={`${p.portfolioId}-${p.tradeId}-${p.message}`}>
                      <Table.Td ta="center">{p.portfolioId}</Table.Td>
                      <Table.Td ta="center">{p.tradeId?.replaceAll(TRADE_ID_PREFIX, "")}</Table.Td>
                      <Table.Td ta="center">{p.message}</Table.Td>
                    </Table.Tr>)
                    // const pid = p.portfolioId;

                    // return (
                    //     <React.Fragment key={pid}>
                    //         {p.map((msg, index) => (
                    //             <Table.Tr key={index}>
                    //                 <Table.Td ta="center">{pid}</Table.Td>
                    //                 <Table.Td ta="center">{msg.tradeId}</Table.Td>
                    //                 <Table.Td ta="center">{msg.warning}</Table.Td>
                    //             </Table.Tr>
                    //         ))}
                    //     </React.Fragment>
                    // );
                  })}
                </Table.Tbody>
              </Table>
            </>
          )}

          {parsedData && (
            <Card withBorder>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={500}>Processing Results</Text>
                  <Badge color="green" variant="light">
                    {parsedData.totalTrades} Trades
                  </Badge>
                </Group>

                <Divider />

                <Group justify="space-between">
                  <Text size="sm">Total Trades:</Text>
                  <Text size="sm" fw={500}>{parsedData.totalTrades}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">Allocated Trades Count:</Text>
                  <Text size="sm" fw={500}>{parsedData.totalAllocations}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">Allocated Trades Qty:</Text>
                  <Text size="sm" fw={500}><NumberFormatter value={parsedData.totalAllocationsQty} thousandSeparator="," decimalScale={3} /></Text>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">Processed Sheets:</Text>
                  <Text size="sm" fw={500}>{parsedData.processedSheets.length}</Text>
                </Group>

                {parsedData.processedSheets.length > 0 && (
                  <>
                    <Text size="sm" fw={500}>Sheets:</Text>
                    <List size="sm">
                      {parsedData.processedSheets.map((sheet, index) => (
                        <List.Item key={index}>
                          <Text size="sm">{sheet}</Text>
                        </List.Item>
                      ))}
                    </List>
                  </>
                )}

                {parsedData.portfolioSummary.size > 0 && (
                  <>
                    <Text size="sm" fw={500} mt="sm">Portfolio Summary:</Text>
                    <List size="sm">
                      {Array.from(parsedData.portfolioSummary.entries()).map(([portfolioId, total]) => (
                        <List.Item key={portfolioId}>
                          <Group justify="space-between">
                            <Text size="sm">Portfolio {portfolioId}:</Text>
                            <Text size="sm" fw={500}>{total.toLocaleString()} Trade</Text>
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </>
                )}
              </Stack>
            </Card>
          )}

          {parsedData && (
            <Group justify="space-between" mt="md">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                loading={loading}
                leftSection={<IconCheck size="1rem" />}
              >
                Confirm & Assign All
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default ExcelUploadComponent;